import { FLAGS } from '@gozoki/feature-flags'
import { FORECAST_REFERENCE_QUERY_OPTIONS } from '~/lib/forecast-reference-queries'
import { Download } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import { buttonVariants } from '~/components/ui/button'
import { Checkbox } from '~/components/ui/checkbox'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import { BacktestPanel } from './-backtest-panel'
import { CorrectionDetail } from './-correction-detail'

export const Route = createFileRoute('/_authenticated/correction-ruptures/')({
  staticData: {
    permission: ['read', 'previsions.previsions.forecast'],
  },
  component: CorrectionRupturesPage,
})
withRoutePermissions(Route)

interface SelectedDay {
  productReference: string
  date: string
}

const defaultFrom = () => {
  const d = new Date()
  d.setDate(d.getDate() - 14)
  return d.toISOString().slice(0, 10)
}

function CorrectionRupturesPage() {
  const enabled = useFeatureFlag(FLAGS.salesUnitsCorrections)
  const [storeId, setStoreId] = useState<number | null>(null)
  const [from, setFrom] = useState(defaultFrom)
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10))
  const [onlyRupted, setOnlyRupted] = useState(true)
  const [selected, setSelected] = useState<SelectedDay | null>(null)

  const { data: stores = [] } = useQuery(
    api.forecast.forecastStores.queryOptions({}, FORECAST_REFERENCE_QUERY_OPTIONS),
  )

  const daysQuery = useQuery(
    api.forecast.salesCorrectionDays.queryOptions(
      { query: { storeId: storeId ?? 0, from, to, onlyRupted } },
      { enabled: storeId !== null },
    ),
  )
  const days = daysQuery.data ?? []

  if (!enabled) {
    return (
      <>
        <AppHeader>
          <Title>Correction des ruptures</Title>
        </AppHeader>
        <AppContainer>
          <p className="text-muted-foreground">Fonctionnalité non activée.</p>
        </AppContainer>
      </>
    )
  }

  return (
    <>
      <AppHeader>
        <div className="flex items-center gap-3">
          <Title>Correction des ruptures</Title>
          <a
            href="/note-correction-ruptures.pdf"
            download
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            <Download className="size-4" />
            Note méthodologique
          </a>
        </div>
      </AppHeader>
      <AppContainer>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <Label>Boutique</Label>
            <Select
              value={storeId === null ? undefined : String(storeId)}
              onValueChange={(value) => {
                setStoreId(Number(value))
                setSelected(null)
              }}
            >
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Choisir une boutique" />
              </SelectTrigger>
              <SelectContent>
                {stores.map((store) => (
                  <SelectItem key={store.storeId} value={String(store.storeId)}>
                    {store.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label>Du</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Au (exclu)</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <label className="mb-2 flex items-center gap-2 text-sm">
            <Checkbox
              checked={onlyRupted}
              onCheckedChange={(checked) => setOnlyRupted(checked === true)}
            />
            Jours ruptés uniquement
          </label>
        </div>

        {storeId !== null && (
          <div className="mt-6">
            <BacktestPanel storeId={storeId} from={from} to={to} />
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
          <div className="max-h-[70vh] overflow-y-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b text-left">
                  <th className="p-2">Date</th>
                  <th className="p-2">Produit</th>
                  <th className="p-2 text-right">UVC</th>
                  <th className="p-2 text-right">Corrigé</th>
                  <th className="p-2">Rupture</th>
                  <th className="p-2">Cause</th>
                </tr>
              </thead>
              <tbody>
                {days.map((day) => {
                  const isSelected =
                    selected?.productReference === day.productReference &&
                    selected?.date === day.date
                  return (
                    <tr
                      key={`${day.productReference}-${day.date}`}
                      className={`cursor-pointer border-b hover:bg-muted ${isSelected ? 'bg-muted' : ''}`}
                      onClick={() =>
                        setSelected({ date: day.date, productReference: day.productReference })
                      }
                    >
                      <td className="p-2 whitespace-nowrap">{day.date}</td>
                      <td className="p-2">{day.productLabel}</td>
                      <td className="p-2 text-right">{day.unitsSold}</td>
                      <td className="p-2 text-right text-primary">
                        {day.correctedUnits === null ? '—' : day.correctedUnits.toFixed(1)}
                      </td>
                      <td className="p-2 whitespace-nowrap">
                        {day.stockoutTime?.slice(0, 5) ?? '—'}
                      </td>
                      <td className="p-2">{day.stockoutDetectionEvent ?? '—'}</td>
                    </tr>
                  )
                })}
                {storeId !== null && days.length === 0 && !daysQuery.isLoading && (
                  <tr>
                    <td className="p-4 text-muted-foreground" colSpan={6}>
                      Aucun jour sur la période.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div>
            {selected && storeId !== null ? (
              <CorrectionDetail
                storeId={storeId}
                productReference={selected.productReference}
                date={selected.date}
              />
            ) : (
              <p className="text-muted-foreground">
                Sélectionne un jour dans la liste pour voir la correction.
              </p>
            )}
          </div>
        </div>
      </AppContainer>
    </>
  )
}
